import React from "react";
import { Box, Link, Typography } from "@mui/material";

export const PRIVACY_POLICY_EFFECTIVE_DATE = "5 June 2026";

export type PolicyBlock =
  | { type: "paragraph"; text: string }
  | { type: "list"; items: string[] }
  | { type: "contact"; email: string };

export type PolicySection = {
  title: string;
  blocks: PolicyBlock[];
};

export const PRIVACY_POLICY_SECTIONS: PolicySection[] = [
  {
    title: "1. Who we are",
    blocks: [
      {
        type: "paragraph",
        text: 'Easy Agility ("we", "us", "our") provides Easy Agility Hub, a UK-based online platform for Kennel Club agility show entries, results, show management, and club administration. We act as the data controller for personal data processed through this website and related Easy Agility services.',
      },
      {
        type: "paragraph",
        text: "If you have any questions about this policy or how we handle your personal data, please contact us:",
      },
      { type: "contact", email: "matt@easyagility.co.uk" },
    ],
  },
  {
    title: "2. What Easy Agility Hub does",
    blocks: [
      {
        type: "paragraph",
        text: "Depending on how you use the service, Easy Agility Hub may allow you to:",
      },
      {
        type: "list",
        items: [
          "create and manage an account",
          "register handlers and dogs",
          "browse shows and submit entries",
          "view show results and entry history",
          "manage show entries as a show organiser",
          "create and administer an agility club",
          "manage club members and training groups",
          "issue and receive club invoices",
          "use sandbox mode to try club features before going live",
        ],
      },
    ],
  },
  {
    title: "3. What information we collect",
    blocks: [
      {
        type: "paragraph",
        text: "We collect personal information that you provide directly, and information generated when you use the service. This may include:",
      },
      {
        type: "list",
        items: [
          "Account information: name, email address, password (stored in hashed form), and account preferences",
          "Handler information: handler names and related account details",
          "Contact information: home address and email address where required for entries or club administration",
          "Dog information: dog name, breed, date of birth, height, grade, Kennel Club registered name, Kennel Club registration number, and related competition details",
          "Show entry information: classes entered, entry references, fees, payment status, and show-related communications",
          "Results and history: competition results linked to you and your dogs",
          "Club administration data: club name, membership, training group membership, training dates, admin roles, and club-related notes",
          "Invoicing and payment information: invoice amounts, billing periods, payment status, and bank or payment details where you provide them for club invoicing or subscriptions",
          "Communications: messages and support requests you send to us",
          "Technical data: authentication tokens, session cookies, browser type, device information, and log data such as IP address and access times",
        ],
      },
      {
        type: "paragraph",
        text: "We do not intentionally collect special category data (such as health information) unless you choose to include it in free-text fields. Please avoid submitting sensitive personal data unless it is necessary.",
      },
    ],
  },
  {
    title: "4. How we use your information",
    blocks: [
      {
        type: "paragraph",
        text: "We use personal data to:",
      },
      {
        type: "list",
        items: [
          "create and manage your account and authenticate you when you sign in",
          "process and manage show entries on your behalf",
          "share entry information with Kennel Club-affiliated show organisers where required to register entries",
          "provide show results and entry history",
          "enable show organisers to manage entries for their events",
          "enable club administrators to manage clubs, members, training groups, and schedules",
          "generate, send, and manage club invoices",
          "operate sandbox mode, including redirecting member emails to the club administrator until a club goes live",
          "process subscription or service payments where applicable",
          "send service-related emails such as password resets, entry confirmations, and invoice notifications",
          "provide customer support and respond to enquiries",
          "maintain the security and proper functioning of the platform",
          "comply with legal, regulatory, and record-keeping obligations",
          "improve and develop our services",
        ],
      },
    ],
  },
  {
    title: "5. Legal basis for processing",
    blocks: [
      {
        type: "paragraph",
        text: "Under UK GDPR, we rely on the following lawful bases:",
      },
      {
        type: "list",
        items: [
          "Contract: to provide the services you sign up for, including entries, club administration, and invoicing",
          "Legal obligation: where we must keep records or provide information to regulators, tax authorities, or competition bodies",
          "Legitimate interests: to operate, secure, and improve Easy Agility Hub, prevent misuse, and support clubs and show organisers, provided those interests are not overridden by your rights",
          "Consent: where required, such as for optional communications. You may withdraw consent at any time",
        ],
      },
    ],
  },
  {
    title: "6. How club and show data is shared",
    blocks: [
      {
        type: "paragraph",
        text: "Some features involve sharing personal data with other users of the platform:",
      },
      {
        type: "list",
        items: [
          "Show organisers may receive handler, dog, and contact details needed to manage entries for their events",
          "Club administrators can view and manage information about members of their club, including names and email addresses",
          "Club members may receive invoices and club-related communications from their club through the platform",
          "When a club is in sandbox mode, emails intended for members may be sent to the club administrator instead",
          "Kennel Club and affiliated show organisers may receive entry information where required for competition registration",
        ],
      },
      {
        type: "paragraph",
        text: "If you are a club administrator, you are responsible for using member data fairly and only for legitimate club administration purposes.",
      },
    ],
  },
  {
    title: "7. Service providers and third parties",
    blocks: [
      {
        type: "paragraph",
        text: "We use trusted service providers to help us run Easy Agility Hub. These may include providers of:",
      },
      {
        type: "list",
        items: [
          "cloud hosting and storage",
          "authentication and identity services",
          "email delivery",
          "payment processing",
          "website infrastructure and security",
        ],
      },
      {
        type: "paragraph",
        text: "These providers process data on our instructions and under appropriate data protection agreements. We do not sell your personal data and we do not share it with third parties for their own marketing purposes.",
      },
      {
        type: "paragraph",
        text: "Our services may link to or integrate with other Easy Agility websites, such as entries and results platforms. Where you use those services, your data may be shared between them as needed to provide the service you request.",
      },
    ],
  },
  {
    title: "8. Cookies and similar technologies",
    blocks: [
      {
        type: "paragraph",
        text: "We use cookies and similar technologies to operate the website. In particular, we use a session cookie to keep you signed in after you log in. This cookie is strictly necessary for the service to function.",
      },
      {
        type: "paragraph",
        text: "You can control cookies through your browser settings. If you disable strictly necessary cookies, parts of the website — including sign-in — may not work properly.",
      },
    ],
  },
  {
    title: "9. International transfers",
    blocks: [
      {
        type: "paragraph",
        text: "We aim to store and process personal data within the United Kingdom. If any service provider processes data outside the UK, we ensure appropriate safeguards are in place in accordance with UK data protection law.",
      },
    ],
  },
  {
    title: "10. How long we keep your data",
    blocks: [
      {
        type: "paragraph",
        text: "We keep personal data only for as long as necessary for the purposes described in this policy, including:",
      },
      {
        type: "list",
        items: [
          "account and profile information while your account remains active",
          "show entry and results records for as long as needed to provide the service and meet competition or regulatory requirements",
          "invoice and financial records for up to 7 years where required for tax, accounting, or legal purposes",
          "support and security logs for a limited period appropriate to their purpose",
        ],
      },
      {
        type: "paragraph",
        text: "We regularly review the data we hold and delete or anonymise information that is no longer needed.",
      },
    ],
  },
  {
    title: "11. Your rights",
    blocks: [
      {
        type: "paragraph",
        text: "Under UK GDPR, you have the right to:",
      },
      {
        type: "list",
        items: [
          "access the personal data we hold about you",
          "request correction of inaccurate data",
          "request erasure of your data in certain circumstances",
          "object to or restrict processing in certain circumstances",
          "request transfer of your data to another service where applicable",
          "withdraw consent at any time where processing is based on consent",
        ],
      },
      {
        type: "paragraph",
        text: "To exercise any of these rights, contact us at matt@easyagility.co.uk. We will respond within one month, unless the request is complex or numerous, in which case we may extend this by up to two further months and will let you know.",
      },
    ],
  },
  {
    title: "12. Children",
    blocks: [
      {
        type: "paragraph",
        text: "Easy Agility Hub may be used to manage entries and records for junior handlers. Accounts should be created and managed by a parent, guardian, or responsible adult. If you believe we have collected personal data from a child without appropriate authority, please contact us and we will take appropriate steps.",
      },
    ],
  },
  {
    title: "13. Data security",
    blocks: [
      {
        type: "paragraph",
        text: "We take data protection seriously and use appropriate technical and organisational measures to protect personal data against loss, misuse, unauthorised access, or disclosure. No online service can be guaranteed completely secure, but we work to maintain a high standard of security.",
      },
    ],
  },
  {
    title: "14. Changes to this policy",
    blocks: [
      {
        type: "paragraph",
        text: "We may update this Privacy Policy from time to time. When we do, we will post the updated version on this page and change the effective date at the top. We encourage you to review this page periodically.",
      },
    ],
  },
  {
    title: "15. Complaints",
    blocks: [
      {
        type: "paragraph",
        text: "If you are unhappy with how we handle your personal data, please contact us first at matt@easyagility.co.uk so we can try to resolve the issue. You also have the right to lodge a complaint with the Information Commissioner's Office (ICO):",
      },
      {
        type: "paragraph",
        text: "Website: https://ico.org.uk",
      },
    ],
  },
];

export const renderPolicyBlock = (
  block: PolicyBlock,
  key: number,
): React.ReactNode => {
  switch (block.type) {
    case "paragraph":
      return (
        <Typography key={key} variant="body1" paragraph sx={{ lineHeight: 1.75 }}>
          {block.text}
        </Typography>
      );
    case "list":
      return (
        <Box component="ul" key={key} sx={{ pl: 3, mt: 0, mb: 2 }}>
          {block.items.map((item) => (
            <Typography
              component="li"
              key={item}
              variant="body1"
              sx={{ mb: 1, lineHeight: 1.75 }}
            >
              {item}
            </Typography>
          ))}
        </Box>
      );
    case "contact":
      return (
        <Typography key={key} variant="body1" paragraph sx={{ lineHeight: 1.75 }}>
          Email:{" "}
          <Link href={`mailto:${block.email}`} underline="hover">
            {block.email}
          </Link>
        </Typography>
      );
    default:
      return null;
  }
};
